import { NavigableButton } from '@carvana/ds-button';
import AppCheck from '@carvana/ds-icons/AppCheckSpot';
import BudgetFriendly from '@carvana/ds-icons/BudgetFriendlySpot';
import SevenDay from '@carvana/ds-icons/SevenDay';
import SmileyCar from '@carvana/ds-icons/SmileyCarSpot';
import { icon } from '@carvana/ds-styles';
import { Fragment } from 'react';
import {
  TileDivider,
  TileHeading,
  TilesWrapper,
  TileText
} from '@/dealership/Hero/Hero.styles';
import { testDriveCenterImages } from '~/constants/dealership/testDriveCenter/assets';
import optimizeImage from '~/utilities/optimizeImage';
import { ShadedBackground } from '../TestDriveCenter.styles';

const section = 'Test Drive Center - Hero';
const heroAlt = 'Brand-new cars parked along the Test Drive Center lanes';

const heroImageOptions = { format: 'auto', optimize: 'medium' } as const;

const valueProps = [
  {
    heading: 'Transparent pricing',
    Icon: BudgetFriendly,
    text: 'Zero doc fees, no hidden fees. See what you’ll pay up front.'
  },
  {
    heading: 'Stress-free test drive',
    Icon: SmileyCar,
    text: 'No upselling, take it for a spin before you commit.'
  },
  {
    heading: 'Buy from anywhere',
    Icon: AppCheck,
    text: 'Buy from our store, or from your couch. We’ll deliver it to your driveway.*'
  },
  {
    heading: '7-Day Money-Back Guarantee**',
    Icon: SevenDay,
    text: 'Try it for a week. If it’s not the right fit, we’ll take it back—no questions asked.'
  }
] satisfies Array<{
  heading: string;
  Icon: React.ComponentType<{ color?: string; size?: number }>;
  text: string;
}>;

export default function TestDriveCenterHero() {
  return (
    <>
      <div className="grid">
        <div className="z-[-1] col-span-full row-span-full flex">
          <picture className="flex w-full">
            <source
              media="(min-width: 1024px)"
              srcSet={optimizeImage(testDriveCenterImages.heroLot, {
                ...heroImageOptions,
                crop: '21:9'
              })}
            />
            <source
              media="(min-width: 768px)"
              srcSet={optimizeImage(testDriveCenterImages.heroLot, {
                ...heroImageOptions,
                crop: '16:9'
              })}
            />
            <img
              src={optimizeImage(testDriveCenterImages.heroLot, {
                ...heroImageOptions,
                crop: '3:4'
              })}
              width={21 * 100}
              height={9 * 100}
              alt={heroAlt}
              className="aspect-[3/4] w-full object-cover md:aspect-[16/9] lg:aspect-[21/9]"
            />
          </picture>
        </div>
        <ShadedBackground />
        <div className="col-span-full row-span-full flex px-24 py-32 md:items-end md:px-32 md:pb-6xl lg:px-[96px]">
          <div className="flex w-full flex-col items-start gap-y-2xl self-end text-left">
            <h1 className="uppercase text-text-inverse t-brand-sm md:t-brand-md lg:t-brand-lg">
              Car Shopping,
              <br />
              Reimagined.
            </h1>
            <p className="text-text-inverse t-body-md lg:t-body-lg">
              Test drive on-site, buy online and get it delivered.
            </p>
            <NavigableButton
              variant="primary"
              size="lg"
              className="w-full md:w-fit"
              href="/cars"
              data-adobe-id="shop-new-cars"
              data-analytics-id={`${section} - Shop New Cars`}
              data-testid="test-drive-center-hero-shop-new-cars"
            >
              Shop New Cars
            </NavigableButton>
          </div>
        </div>
      </div>
      <div className="bg-background-strong px-2xl py-4xl text-text-inverse-subtle sm:py-5xl md:px-3xl md:py-7xl lg:px-4xl">
        <TilesWrapper>
          {valueProps.map(({ heading, Icon, text }, index) => (
            <Fragment key={heading}>
              {index > 0 && <TileDivider />}
              <div className="flex gap-x-xl md:flex-col md:gap-y-lg">
                <span className="flex-none md:hidden">
                  <Icon color={icon.inverse} size={32} />
                </span>
                <span className="hidden md:block">
                  <Icon color={icon.inverse} size={56} />
                </span>
                <div className="flex flex-col gap-y-lg">
                  <TileHeading>{heading}</TileHeading>
                  <TileText>{text}</TileText>
                </div>
              </div>
            </Fragment>
          ))}
        </TilesWrapper>
      </div>
    </>
  );
}
